import {CSSProperties, useState, useEffect} from 'react';
import {Person, ContactDetailsValidation} from '../lib/interfaces';
import {t} from 'i18next';
import {isValidEmail, isValidPersonalIdentityNumber} from '../helpers/validations';
import {useBookingState} from '../store';
import {
  ADDRESS_FIELD_MAX_LENGTH,
  CITY_FIELD_MAX_LENGTH,
  COUNTRY_FIELD_MAX_LENGTH,
  EMAIL_FIELD_MAX_LENGTH,
  FIRSTNAME_FIELD_MAX_LENGTH,
  LASTNAME_FIELD_MAX_LENGTH,
  PHONE_FIELD_MAX_LENGTH,
  ZIPCODE_FIELD_MAX_LENGTH,
} from '../utils/constants';
import {UaCheckbox, UaDateInput, UaInput} from 'uppsala.ui.react';

interface ContactDetailsProps {
  personTitle: string;
  person: Person;
  personalIdentityNumberDisabled?: boolean;
  style?: CSSProperties;
  personNumber: 1 | 2;
  highlightMissingFields?: boolean;
  onInputChange: (value: string | number | boolean, id: string) => void;
  onPersonExtendedChange: (value: string | number | boolean, id: string) => void;
  resetPersonExtended?: () => void;
}

export const ContactDetails = (props: ContactDetailsProps) => {
  const person1State = useBookingState((state) => state.person1);
  const validationFields = useBookingState((state) => state.validationFields);
  const setValidationFields = useBookingState((state) => state.setValidationFields);
  const [showPersonExtended, setShowPersonExtended] = useState(false);

  useEffect(() => {
    if (props.person.personExtended) {
      setShowPersonExtended(true);
    }
  }, []);

  function validationCheck(
    value: string | number | boolean | undefined,
    validationMessage: string,
    nameKey: string,
    fieldMaxLength: number = 128
  ) {
    if (!value || (typeof value === 'string' && value.length === 0)) {
      setValidationFields(removeValidationFromList(nameKey));
      return;
    }

    let valid = true;
    let parameter = '';

    // Match the validation to the correct field
    switch (nameKey) {
      case 'email':
        valid = isValidEmail(value as string);
        break;
      case 'personalIdentityNumber':
        valid = isValidPersonalIdentityNumber(value as string);
        if (props.personNumber === 2 && valid) {
          valid = value !== person1State.personalIdentityNumber;
          validationMessage = !valid ? 'validation.samePIN' : '';
        }
        break;
      default:
        break;
    }

    if (typeof value === 'string' && value.length > fieldMaxLength) {
      valid = false;
      validationMessage = 'validation.fieldExceededMaxLength';
      parameter = fieldMaxLength.toString();
    }

    // Replace the validation in the list with the new one
    const updatedList = removeValidationFromList(nameKey);
    setValidationFields([
      ...updatedList,
      {
        personNumber: props.personNumber,
        nameKey: nameKey,
        isValid: valid,
        message: t(validationMessage, {parameter}),
      } as ContactDetailsValidation,
    ]);
  }

  function removeValidationFromList(nameKey: string) {
    return validationFields.filter((x) => {
      if (x.personNumber === props.personNumber) {
        return x.nameKey !== nameKey;
      }
      return x;
    });
  }

  function retrieveValidationFromList(nameKey: string): ContactDetailsValidation | undefined {
    const validation = validationFields.find((x) => x.nameKey === nameKey && x.personNumber === props.personNumber);

    // If highlightMissingFields is true, we want to show the validation message that the field is missing with some exceptions
    if (props.highlightMissingFields && !validation?.isValid) {
      if (validation?.message) return validation;
      if (nameKey === 'personalIdentityNumber' && props.personNumber === 1) return undefined;
      if (nameKey === 'personalIdentityNumber' && props.personNumber === 2 && showPersonExtended) return undefined;

      return {isValid: false, message: t('validation.missingField')} as ContactDetailsValidation;
    }
    return validation;
  }

  return (
    <div className="form-group" style={props.style}>
      <h2>{props.personTitle}</h2>

      <UaInput
        id={'personalIdentityNumber' + '_' + props.personNumber.toString()}
        value={props.person.personalIdentityNumber}
        label={t('booking.form.personalIdentityNumber')}
        disabled={props.personalIdentityNumberDisabled ?? showPersonExtended}
        style={{marginTop: '5px'}}
        onChange={(e) => {
          props.onInputChange(e.target.value, 'personalIdentityNumber');
          validationCheck(e.target.value, 'validation.invalidPIN', 'personalIdentityNumber');
          const isValid = isValidPersonalIdentityNumber(e.target.value);
          if (isValid && props.personNumber === 2) props.resetPersonExtended?.();
        }}
        valid={retrieveValidationFromList('personalIdentityNumber')?.isValid}
        invalidMessage={retrieveValidationFromList('personalIdentityNumber')?.message}
      />

      {!props.person.personalIdentityNumber && (
        <UaCheckbox
          id="showPersonExtended"
          text={
            t('booking.form.missingPIN', {
              personTitle: props.personTitle,
            }) ?? 'Personen saknar personnummer'
          }
          defaultChecked={showPersonExtended}
          onInputChange={() => {
            props.onInputChange('', 'personalIdentityNumber');
            setShowPersonExtended(!showPersonExtended);
          }}
        />
      )}

      <UaInput
        id={'telephoneNumber' + '_' + props.personNumber.toString()}
        value={props.person.phone ?? ''}
        label={t('booking.form.telephoneNumber')}
        onChange={(e) => {
          props.onInputChange(e.target.value, 'phone');
          validationCheck(e.target.value, 'validation.invalidPhoneNumber', 'phone', PHONE_FIELD_MAX_LENGTH);
        }}
        valid={retrieveValidationFromList('phone')?.isValid}
        invalidMessage={retrieveValidationFromList('phone')?.message}
      />

      <UaInput
        id={'email' + '_' + props.personNumber.toString()}
        value={props.person.email ?? ''}
        type="email"
        label={t('booking.form.email')}
        onChange={(e) => {
          props.onInputChange(e.target.value, 'email');
          validationCheck(e.target.value, 'validation.invalidEmail', 'email', EMAIL_FIELD_MAX_LENGTH);
        }}
        valid={retrieveValidationFromList('email')?.isValid}
        invalidMessage={retrieveValidationFromList('email')?.message}
      />

      {showPersonExtended && (
        <>
          <UaInput
            id={'firstName' + '_' + props.personNumber}
            value={props.person.personExtended?.firstName}
            label={t('booking.form.firstName')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'firstName');
              validationCheck(e.target.value, 'validation.invalidFirstName', 'firstName', FIRSTNAME_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('firstName')?.isValid}
            invalidMessage={retrieveValidationFromList('firstName')?.message}
          />
          <UaInput
            id={'lastName' + '_' + props.personNumber}
            value={props.person.personExtended?.lastName}
            label={t('booking.form.lastName')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'lastName');
              validationCheck(e.target.value, 'validation.invalidLastName', 'lastName', LASTNAME_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('lastName')?.isValid}
            invalidMessage={retrieveValidationFromList('lastName')?.message}
          />
          <UaInput
            id={'address' + '_' + props.personNumber}
            value={props.person.personExtended?.address}
            label={t('booking.form.address')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'address');
              validationCheck(e.target.value, 'validation.invalidAddress', 'address', ADDRESS_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('address')?.isValid}
            invalidMessage={retrieveValidationFromList('address')?.message}
          />
          <UaInput
            id={'postalCode' + '_' + props.personNumber}
            value={props.person.personExtended?.zipCode}
            label={t('booking.form.postalCode')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'zipCode');
              validationCheck(e.target.value, 'validation.invalidPostalCode', 'zipCode', ZIPCODE_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('zipCode')?.isValid}
            invalidMessage={retrieveValidationFromList('zipCode')?.message}
          />
          <UaInput
            id={'city' + '_' + props.personNumber}
            value={props.person.personExtended?.city}
            label={t('booking.form.city')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'city');
              validationCheck(e.target.value, 'validation.invalidCity', 'city', CITY_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('city')?.isValid}
            invalidMessage={retrieveValidationFromList('city')?.message}
          />
          <UaInput
            id={'country' + '_' + props.personNumber}
            value={props.person.personExtended?.country}
            label={t('booking.form.country')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value, 'country');
              validationCheck(e.target.value, 'validation.invalidCountry', 'country', COUNTRY_FIELD_MAX_LENGTH);
            }}
            valid={retrieveValidationFromList('country')?.isValid}
            invalidMessage={retrieveValidationFromList('country')?.message}
          />
          <UaDateInput
            id={'dateOfBirth' + '_' + props.personNumber}
            value={props.person.personExtended?.dateOfBirth}
            label={t('booking.form.dateOfBirth')}
            onChange={(e) => {
              props.onPersonExtendedChange(e.target.value ?? '', 'dateOfBirth');
              validationCheck(e.target.value, 'validation.missingField', 'dateOfBirth');
            }}
            valid={retrieveValidationFromList('dateOfBirth')?.isValid}
            invalidMessage={retrieveValidationFromList('dateOfBirth')?.message}
          />
        </>
      )}
    </div>
  );
};
